/**
 * THE ASSIGNMENTS ROW MODEL — the group shape the screen builds, and the flat
 * rows its one table renders.
 *
 * Pulled out of `screens/AssignmentsScreen.tsx` on 2026-09-22 (QA wave B1,
 * B1-01) for the reason `lib/saveOnBlur.ts` and `lib/readState.ts` were: this
 * is where a row's own APPOINT AUTHORITY is decided, a component module may
 * export only components, and a rule nobody can call from a test is a rule
 * nobody checks. The defect that forced it — the one organization-wide
 * Oversight Approval seat could be emptied and never refilled — lived entirely
 * in `rowsOf` and was invisible in every layer either side of it.
 *
 * Nothing here renders. The screen keeps the data loading, the writes and the
 * dialogs.
 */
import { ASSIGNMENT_ROLE_TO_ROLE, pipelineStepNumber } from './constants'
import type { AssignmentRole, Role } from './constants'
import { NOTHING_CHANGED, sentenceList } from './format'
import { isApiError } from '../api/errors'

/** What the Company column reads for a row that is not about one company, on a
    screen that HAS that column (owner, 2026-09-14: "For the universal/
    centralized roles, like Import Control, maybe the Company value just says all
    or something"). On Invoices the internal step roles and the org-wide
    Oversight seat sit beside per-company tiers, so a blank cell would read as a
    fact nobody had filled in. References, Rules and Assignments show it on
    every row since 2026-10-07 (owner: "add to the table a Company column that
    just says 'All'"). See `AssignmentRow.company`.

    A WORD ON A SCREEN, NEVER A COMPANY ID. It reads the same as the `All` row
    that used to sit in `companies` (D-134 deleted it), and the resemblance is a
    coincidence worth not repeating: nothing may send this string to the API as
    a `company_id`. An org-wide appointment sends no company at all. */
export const ALL_COMPANIES = 'All'

/** One line in a group: an internal-role holder, or an appointment. */
export interface Seat {
  key: string
  name: string
  email?: string
  /** Company groups only — Oversight approver / Director / Department head /
      Fleet. There is no separate Oversight group any more: its seat sits in the
      company group with the rest (issue 2, 2026-08-10). */
  roleLabel?: string
  /** "From <date>" — when this assignment began. Shown in the row's own editor.
      An internal-role grant has no such date and shows nothing. */
  started?: string
  /** False for a retired/rejected row, which is history, not authority. */
  live: boolean
  canRemove: boolean
  /** Which write removing this seat is — an internal `user_role` grant or a
      company assignment. It decides the sentence the caller says afterwards,
      which is the caller's job now and not the remover's (QA wave B1, B1-10). */
  kind?: 'role' | 'assignment'
  /** The write ALONE: no toast, no busy flag, no banner. Rejects on refusal,
      and the caller decides what that means — `removeSeat` shows it in the
      dialog, `submitUnassign` collects it into one summary. */
  remove?: () => Promise<void>
  /** Who holds it, as a directory record: what adding the same person to
      another row writes (the bulk pop-up's "add to the rest", D-341). */
  person?: { id: string; upn: string; name: string; email: string }
}

/** One person, whichever seats they hold: the upn where known, else the name.
    The bulk pop-up groups a person's seats across the selected rows on it. */
export function personKey(seat: Pick<Seat, 'name' | 'person'>): string {
  return (seat.person?.upn || seat.name).toLowerCase()
}

/**
 * WHAT ONE SAVE OF A PEOPLE FIELD SAYS (D-341). A pop-up now adds and removes
 * several people in one Apply, so the toast names who was added, then says what
 * was removed in `removalToast`'s own words (B-14), so the two halves still read
 * the way a single add and a single removal always have.
 */
export function peopleSavedToast(
  added: readonly string[],
  where: string,
  removed: { roles: number; assignments: number },
  skipped = 0,
): string {
  const parts: string[] = []
  if (added.length === 1) parts.push(`${added[0]} was added to ${where}.`)
  else if (added.length > 1) parts.push(`${sentenceList(added)} were added to ${where}.`)
  if (skipped > 0) parts.push(bulkSkippedSentence(skipped))
  if (removed.roles + removed.assignments > 0) parts.push(removalToast(removed))
  return parts.length > 0 ? parts.join(' ') : NOTHING_CHANGED
}

/**
 * THE SENTENCE A REMOVAL SAYS — one seat from its row's editor, or many from
 * the bulk Unassign — in one vocabulary (QA wave B, 2026-09-25, B-14; D-170).
 * The bulk path used to say *"Emerson Blake was removed from 1 role."* where
 * the row editor, for the same act, said *"The assignment is now Inactive."*
 *
 * A company assignment is made Inactive (kept, stamped); an internal role
 * grant is removed. One of a kind reads as the editor's own sentence, several
 * as its plural, and a removal that took both says both.
 */
export function removalToast(counts: { roles: number; assignments: number }): string {
  const parts: string[] = []
  if (counts.assignments === 1) parts.push('The assignment is now Inactive.')
  else if (counts.assignments > 1) parts.push(`${counts.assignments} assignments are now Inactive.`)
  if (counts.roles === 1) parts.push('The role was removed.')
  else if (counts.roles > 1) parts.push(`${counts.roles} roles were removed.`)
  return parts.length > 0 ? parts.join(' ') : NOTHING_CHANGED
}

/**
 * WHAT A BULK SAVE ADDED, PER PERSON (D-341). Someone already on two of three
 * ticked rows is added to one, so the toast says "1 role", not the selection's
 * size: people added to the same number of rows share a sentence, largest
 * first — "Alex Morgan and Bailey Nakamura were added to 3 roles. Casey
 * Okonkwo was added to 1 role."
 */
export function bulkSavedToast(
  added: ReadonlyMap<string, number>,
  removed: { roles: number; assignments: number },
  skipped = 0,
): string {
  const byCount = new Map<number, string[]>()
  for (const [name, n] of added) if (n > 0) byCount.set(n, [...(byCount.get(n) ?? []), name])
  const parts = [...byCount.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([n, names]) =>
      peopleSavedToast(names, n === 1 ? '1 role' : `${n} roles`, { roles: 0, assignments: 0 }),
    )
  if (skipped > 0) parts.push(bulkSkippedSentence(skipped))
  if (removed.roles + removed.assignments > 0) parts.push(removalToast(removed))
  return parts.length > 0 ? parts.join(' ') : NOTHING_CHANGED
}

/** "A will be removed from X. B will be removed from Y and Z." — each person
    from the rows they actually hold, never the whole selection (D-341). */
export function removalSentences(people: readonly { name: string; from: readonly string[] }[]): string {
  return people.map((p) => `${p.name} will be removed from ${sentenceList(p.from)}.`).join(' ')
}

/**
 * THE SENTENCE WHEN A SAVE WAS PARTLY REFUSED — what did land, then the
 * refusals, each with its row or person (second review of QA wave B,
 * 2026-09-25; every People Field Apply since D-341). *"2 assignments are now
 * Inactive. 1 was refused: …"*; with nothing landed, *"Nothing was changed. 2
 * were refused: …"*.
 */
export function refusedMessage(landed: string, failures: readonly string[]): string {
  return `${landed} ${failures.length} ${failures.length === 1 ? 'was' : 'were'} refused: ${failures.join('; ')}`
}

/** What an Appoint control on a group will do when submitted. */
export type Target =
  | {
      kind: 'role'
      role: Role
      title: string
      /** Set only on References and Rules (owner item 29, 2026-08-28), whose
          groups carry one sub-role per tab the way a company card carries one
          per approval tier. When present the dialog offers a Role picker over
          these and `role` above is only the default; when absent the group
          grants exactly one role and there is nothing to pick. */
      roleChoices?: Role[]
      /** Every role this target grants, where that is more than its identity —
          today only Transportation, whose tab holds the fleet as well as its own
          category vocabulary (owner, 2026-09-15). `submitAppoint` writes all of
          them under one action, so the reader appoints the row they see rather
          than half of it. Defaults to the single granted role. */
      grantRoles?: Role[]
    }
  | {
      kind: 'assignment'
      roleChoices: AssignmentRole[]
      companyId?: string
      title: string
    }

/** One approval tier's holders inside a company card (owner item 13,
    2026-08-13). Populated only in `companyGroups` — an internal-role group
    (Admin/Accounting) has no tiers, so it has no `seatsByRole`. */
export interface RoleSeats {
  role: string
  /** The roles this row GRANTS, where that is more than its identity — today
      only Transportation (owner, 2026-09-15). Absent on every company tier,
      which grants exactly its own `role`. */
  roles?: Role[]
  label: string
  seats: Seat[]
  // `note` — a line under a tier heading about what the seat also granted —
  // went with the Fleet Manager row on 2026-09-14 (owner). It was set on that
  // seat and on nothing else, so nothing is left to carry.
}

export interface Group {
  key: string
  title: string
  // NO `blurb` AND NO `note` (owner, 2026-09-14: "in Assignments, let's drop
  // the help text under each role"). A group carried its screen's own
  // subheader as a line under the title, and References carried a second line
  // explaining the two tabs it did not appoint. The subheader repeated what
  // the screen's own header already said one row up, and the second line
  // described an absence that no longer exists — every References tab is
  // appointed here now. `SCREEN_SUBHEADER` still feeds each screen's own
  // header; nothing on this screen repeats it.
  seats: Seat[]
  /** Set only on a company card. When present, `GroupCard` renders one
      sub-section per tier (Director and Department Head) — each with its own
      holders or
      an explicit "No one assigned." — instead of the single flat list `seats`
      still carries (kept, unchanged, for the Find-box text match and the
      live-count badge; `seats` is not restructured in place). Absent for
      every other group, which still renders the flat list it always has. */
  seatsByRole?: RoleSeats[]
  /** Absent when the signed-in user may not appoint here. The group still
      renders — see the file header. */
  target?: Target
  /** A company missing a default-path approver is called out rather than
      hidden. Set only where the viewer may appoint — see `noApproverWarning`
      below for why. */
  warnEmpty?: string
  /** The default-path seats this company has nobody in (D-336, amended
      2026-10-09), as `assignment_role` tokens. Only those rows carry the
      warning sentence; every row of the company is still `flagged`. */
  missingRoles?: readonly string[]
}

/** ONE ROW OF THE ASSIGNMENTS TABLE: one role, on one company (or on all of
    them), and whoever holds it.
 *
 *  This is the grain every Assignments screen reduces to, and it is the grain
 *  that makes the screen filterable and sortable — "show me Manager Approval"
 *  and "show me PACE Healthcare Transportation" are a column each. */
export interface AssignmentRow {
  key: string
  /** Where this role sits in the pipeline, 1-based. Only on the Invoices
      screen, and only for a role with a `TIMELINE_NODES` entry. */
  step?: number
  /** The role's own name — "Manager Approval", "Vendors", "Import Control". */
  role: string
  // `roleHelp` — one line of gloss under the role's name — is gone (owner,
  // 2026-09-14: "let's drop the help text under each role"). The role names
  // are the app's own step and tab names; a sentence explaining each of them
  // under every row is a paragraph of screen for something the reader already
  // knows by the time they are appointing to it.
  //
  // ABSENT ON A ROW THAT IS NOT ABOUT A COMPANY (owner, 2026-09-22): *"The
  // Company column does not belong on any of these Roles, outside the Invoices
  // sub-tab."* References, Rules and Assignments grant
  // organization-wide permissions — there is no per-company version of "may edit
  // the Codes rules" — so every row on those four leaves this undefined and the
  // screen drops the filter. (The column itself came back on References, Rules
  // and Assignments on 2026-10-07, every cell reading `All` — the screen's
  // `ALL_COMPANY_SCREENS` — and this stays undefined.) It used to be set
  // to `ALL_COMPANIES` on every such row, which is a value dressed as a fact.
  // ANALYTICS HAS IT AGAIN since 2026-10-01 (D-258): its Dashboard and Reporting
  // seats are per company now, so its cards are company cards.
  company?: string
  /** The company's id beside its name, so the search matches the code too
      (owner: records show by name, and searches also match the ID). Set
      exactly where `company` is. */
  companyId?: string
  seats: Seat[]
  /** Opens the dialog for this row's ROLE alone, when the viewer may fill it. */
  fill?: Target
  /** This row's company is missing a default-path approver, so the Approvers
      filter and Show These Companies keep it. */
  flagged?: boolean
  /** The warning sentence, on the row of each empty default-path seat only. */
  flagNote?: string
}

/** Flatten the group model — which is where the authority, the seats and the
    warnings are already worked out — into one row per role.
 *
 *  A group with no `seatsByRole` IS one role, named by the group: Administration's
 *  Admin, each of the five internal steps. It yields a single row. A group WITH
 *  them yields one row per role. Neither carries help text any more (owner,
 *  2026-09-14). */
export function rowsOf(groups: Group[]): AssignmentRow[] {
  return groups.flatMap((g) => {
    const isCompany = g.key.startsWith('company:')
    // UNDEFINED, not `ALL_COMPANIES`. A row that is not about a company has no
    // company, and the screen decides from that whether the column exists at
    // all — rather than the column existing everywhere and a word standing in.
    const company = isCompany ? g.title : undefined
    const companyId = isCompany ? g.key.slice('company:'.length) : undefined
    const t = g.target
    // A group with no per-role breakdown IS one role, and that role is its own
    // `Role` value — NOT the group key, which is prefixed `role:`. Using the key
    // here made every centralized row fail the authority check below and read
    // "You do not have permission", because no `roleChoices` list contains
    // `role:ImportControl`.
    const slots: RoleSeats[] = g.seatsByRole ?? [
      { role: g.key.replace(/^role:/, ''), label: g.title, seats: g.seats },
    ]
    return slots.map((sl): AssignmentRow => {
      // `roleChoices` is the authority mirror the card's Assign button used:
      // the row offers a fill ONLY for a role this viewer may appoint, and
      // passes a subset of one — never a wider list. A widening here would
      // offer a control the API refuses.
      // A `role` target carries `roleChoices` only where the group offers a
      // CHOICE — References, Rules, Analytics. A group that grants exactly one
      // role (Admin, each of the five steps) carries `role` alone, and the row
      // may be filled when it names that role.
      const mayFill =
        t !== undefined &&
        (t.kind === 'assignment'
          ? t.roleChoices.includes(sl.role as AssignmentRole)
          : t.roleChoices
            ? t.roleChoices.includes(sl.role as Role)
            : sl.role === t.role)
      const fill =
        t && mayFill
          ? t.kind === 'assignment'
            ? { ...t, roleChoices: [sl.role as AssignmentRole] }
            : t.roleChoices
              ? { ...t, roleChoices: [sl.role as Role], grantRoles: sl.roles ?? [sl.role as Role] }
              : t
          : undefined
      // A company tier arrives as an `assignment_role` token and a centralized
      // role as a `Role`; both reach `TIMELINE_NODES` through the same helper.
      // The map is consulted for EVERY slot, not only a company one (QA wave
      // B1, 2026-09-22): the organization-wide `oversight` row is not a company
      // group and still names an assignment role.
      const asRole = ASSIGNMENT_ROLE_TO_ROLE[sl.role] ?? (sl.role as Role)
      return {
        key: `${g.key}:${sl.role}`,
        step: asRole ? pipelineStepNumber(asRole) : undefined,
        role: sl.label,
        company,
        companyId,
        // A-Z BY NAME (owner, 2026-10-14: "I would like a stack of people in an
        // Assigned To cell to be sorted alphabetically"). They arrived in the
        // order the server lists assignments, which is not one a reader can use.
        seats: [...sl.seats].sort((x, y) => x.name.localeCompare(y.name, undefined, { sensitivity: 'base' })),
        fill,
        flagged: isCompany && !!g.warnEmpty,
        flagNote: isCompany && g.warnEmpty && g.missingRoles?.includes(sl.role) ? g.warnEmpty : undefined,
      }
    })
  })
}

/** What a row is, for a screen reader and for a toast: the role, and the
    company where the row is about one. */
/** "Manager Approval for SeniorLIFE Altoona"; "Import Control for all
    companies" on an organization-wide row (owner, 2026-10-09: 'switch "on" to
    "for", and let's be consistent and say for example "Import Control for all
    companies"'). */
export function rowName(row: AssignmentRow): string {
  return `${row.role} for ${row.company ?? 'all companies'}`
}

/** The same name as a FIELD LABEL, every word capitalized: "Manager Approval
    For SeniorLIFE Altoona", "Import Control For All Companies" (owner,
    2026-10-12 — D-351: "all input headers/labels 'Capitalize Each Word' across
    all modals"). The Edit Assignments pop-up's People Field reads it. Only the
    words this function adds change case; the company name is a stored
    identifier and is shown exactly as stored (`Seniors at Home` keeps its
    lower-case "at"). Sentences and toasts keep `rowName`. */
export function rowLabel(row: AssignmentRow): string {
  return `${row.role} For ${row.company ?? 'All Companies'}`
}

/**
 * DOES THIS PERSON ALREADY HOLD THIS SEAT? (owner, 2026-09-29: "in Assignments
 * I am able to add the same person to the same role more than once").
 *
 * A company seat is one live appoint row for the same role, company and
 * person; an internal role is one active grant of the same token. The server
 * refuses both with 409 `duplicate_key` — this only lets the screen say so
 * before it writes, and lets a bulk add skip a row the person already holds
 * rather than report it as a failure.
 */
export function alreadyHolds(
  held: {
    assignments: readonly {
      action: string
      role: string
      company_id?: string | null
      user_id: string
      workspace: string
      is_active?: boolean | null
      retired_at?: string | null
      request_status?: string | null
    }[]
    userRoles: readonly { user_object_id: string; workspace: string; role: string; is_active?: boolean }[]
  },
  userId: string,
  workspace: string,
  seat: { kind: 'assignment'; role: string; companyId: string | null } | { kind: 'role'; token: string },
): boolean {
  if (seat.kind === 'role') {
    return held.userRoles.some(
      (r) => r.is_active !== false && r.user_object_id === userId && r.workspace === workspace && r.role === seat.token,
    )
  }
  return held.assignments.some(
    (a) =>
      a.action === 'appoint' &&
      // The server's live test (`activeForUser`): an old Pending row is not a seat.
      (a.request_status == null || a.request_status === 'Applied') &&
      !!a.is_active &&
      !a.retired_at &&
      a.user_id === userId &&
      a.workspace === workspace &&
      a.role === seat.role &&
      (a.company_id ?? null) === seat.companyId,
  )
}

/**
 * RUN ONE GRANT, AND READ THE SERVER'S "ALREADY HOLDS" AS A SKIP (owner,
 * 2026-10-14: a person already holding some of the picked roles "is not really
 * an error"). The server refuses a seat the person already holds with 409
 * `duplicate_key` — on an appointment and on an internal role alike — and that
 * refusal means exactly what `alreadyHolds` would have said had the screen
 * loaded the seat. True when the grant landed, false when it was already held;
 * any other refusal still throws.
 */
export async function unlessAlreadyHeld(grant: () => Promise<unknown>): Promise<boolean> {
  try {
    await grant()
    return true
  } catch (e) {
    if (isApiError(e) && e.status === 409 && e.code === 'duplicate_key') return false
    throw e
  }
}

/** "Already held" in a bulk add's summary: one sentence, singular or plural. */
function bulkSkippedSentence(skipped: number): string {
  return skipped === 1 ? '1 was already held.' : `${skipped} were already held.`
}

/**
 * The per-company sentence behind each empty seat's marker and the banner
 * count, naming the default-path seats nobody holds, in pipeline order (D-336,
 * amended 2026-10-09). Every required step waits when nobody is seated, so a
 * company missing either Manager Approval or Director Approval strands every
 * invoice no rule routes elsewhere.
 */
export function missingApproverSentence(missingLabels: readonly string[]): string {
  const [first, second] = missingLabels
  if (second === undefined) {
    return `This company has no ${first} approver. Its invoices will wait at ${first} until someone is assigned.`
  }
  return (
    `This company has no ${first} or ${second} approver. ` +
    `Its invoices will wait at ${first}, and then at ${second}, until someone is assigned to each.`
  )
}

/**
 * A company's missing-approver warning, or `undefined`. `missingLabels` is the
 * default-path seats with nobody live in them, in pipeline order. Keyed on
 * whether the viewer may appoint SOMEWHERE, not on this company (docs/app.md
 * §6 Assignments; QA 2026-10-04 B-04): a director who may staff only their own
 * companies was told nothing about the twenty with nobody, because their own
 * all had approvers.
 */
export function noApproverWarning(
  viewerMayAppointSomewhere: boolean,
  missingLabels: readonly string[],
): string | undefined {
  return viewerMayAppointSomewhere && missingLabels.length > 0 ? missingApproverSentence(missingLabels) : undefined
}
